import type { PlaybackStats } from '../../components/video_v8/types';
import { StreamQuality } from '../../store/slices/settings/types';

export interface VideoPlayerConfig {
  sessionId: string;
  videoPlayerVersion: number;
  videoPlayerIOSVersion: number;
  videoLocation: string;
  hlsLocation: string;
  supportsAV1Codec?: boolean;
  codecString?: string;
  iosVideoBandwidthConfig?: number[];
  iosAbrConfig?: number[];
}

export interface VideoStatLoggerConfig {
  url: string;
  userId: string;
  countryCode: string;
  tableId: string;
}

export interface VideoPlayerStartConfig {
  videoStatLoggerConfig: VideoStatLoggerConfig | null;
  storeVideoQuality: boolean;
  statsBasedVideoQuality: number | null;
}

export interface VideoServiceConfig extends VideoPlayerStartConfig {
  videoPlayerConfig: VideoPlayerConfig;
}

export type VideoControllerVersion = 1 | 2 | 3 | 4 | 5;

export type UninitialisedVideoControllerVersion = VideoControllerVersion | 0;

export const HostEvent = {
  EnableVerboseLogging: 'video:enable-verbose-logging',
  GetChannelLimit: 'video:get-channel-limit',
  GetPlaybackStats: 'video:get-playback-stats',
  SetChannel: 'video:set-channel',
  SetVolume: 'video:set-volume',
  Setup: 'video:setup',
  Start: 'video:start',
  Stop: 'video:stop',
} as const;

export type HostEventName = (typeof HostEvent)[keyof typeof HostEvent];

export interface HostEventPayload {
  [HostEvent.EnableVerboseLogging]: void;
  [HostEvent.GetChannelLimit]: void;
  [HostEvent.GetPlaybackStats]: void;
  [HostEvent.SetChannel]: StreamQuality;
  [HostEvent.SetVolume]: number;
  [HostEvent.Setup]: { config: VideoPlayerConfig };
  [HostEvent.Start]: VideoPlayerStartConfig;
  [HostEvent.Stop]: void;
}

export const VideoPlayerEvent = {
  ChannelLimit: 'video:channel-limit',
  ForcedAutoChannelSwitch: 'video:forced-auto-channel-switch',
  ResetStreamQualityLimit: 'video:reset-stream-quality-limit',
  LatencyChange: 'video:latency-change',
  PlaybackStats: 'video:playback-stats',
  PlayerLoaded: 'video:player-loaded',
  Playing: 'video:playing',
} as const;

export type VideoPlayerEventName = (typeof VideoPlayerEvent)[keyof typeof VideoPlayerEvent];

export interface VideoPlayerEventPayload {
  [VideoPlayerEvent.ChannelLimit]: StreamQuality;
  [VideoPlayerEvent.ForcedAutoChannelSwitch]: number;
  [VideoPlayerEvent.ResetStreamQualityLimit]: number;
  [VideoPlayerEvent.LatencyChange]: number;
  [VideoPlayerEvent.PlaybackStats]: PlaybackStats;
  [VideoPlayerEvent.PlayerLoaded]: { version: VideoControllerVersion; name: string; videoElement: HTMLVideoElement };
  [VideoPlayerEvent.Playing]: { playing: boolean };
}

export const VideoPlayerHandshakeEvent = {
  AwaitingContainer: 'video:awaiting-container',
  HostContainer: 'video:host-container',
  Ready: 'video:ready',
} as const;

export type VideoPlayerHandshakeEventName = (typeof VideoPlayerHandshakeEvent)[keyof typeof VideoPlayerHandshakeEvent];

export interface VideoPlayerHandshakeEventPayload {
  [VideoPlayerHandshakeEvent.AwaitingContainer]: void;
  [VideoPlayerHandshakeEvent.HostContainer]: { container: HTMLElement };
  [VideoPlayerHandshakeEvent.Ready]: void;
}
